import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Search, X, Mail, Phone, Building2,
  Calendar, MessageSquare, Trash2, Check, AlertTriangle, ShieldCheck,
} from "lucide-react";
import SEO from "../components/SEO";
import { API } from "../apiBase";
const STATUSES = [
  { v: "new", label: "Nouvelle", color: "border-[rgba(127,179,255,0.4)] bg-[rgba(127,179,255,0.08)] text-[#0284C7]" },
  { v: "contacted", label: "Contactée", color: "border-[rgba(168,140,255,0.4)] bg-[rgba(168,140,255,0.08)] text-[#7C3AED]" },
  { v: "qualified", label: "Qualifiée", color: "border-[rgba(255,170,80,0.4)] bg-[rgba(255,170,80,0.08)] text-[#ffce99]" },
  { v: "won", label: "Gagnée", color: "border-[rgba(110,231,197,0.5)] bg-[rgba(110,231,197,0.10)] text-[var(--mc-mint)]" },
  { v: "lost", label: "Perdue", color: "border-[rgba(180,180,180,0.4)] bg-[rgba(180,180,180,0.06)] text-[#bbb]" },
  { v: "spam", label: "Spam", color: "border-[rgba(255,120,120,0.4)] bg-[rgba(255,120,120,0.06)] text-[#DC2626]" },
];
const META = Object.fromEntries(STATUSES.map((s) => [s.v, s]));

export default function AdminDemoRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/demo-requests`);
      setItems(data || []);
    } catch (e) {
      setErr("Impossible de charger les demandes.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/demo-requests/${id}/status`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) {
      alert(e?.response?.data?.detail || "Mise à jour impossible.");
    }
  };

  const deleteOne = async (id, who) => {
    if (!window.confirm(`Supprimer définitivement la demande de « ${who} » ?`)) return;
    try {
      await axios.delete(`${API}/demo-requests/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const filtered = useMemo(() => {
    let arr = items;
    if (filter !== "all") arr = arr.filter((x) => (x.status || "new") === filter);
    if (!query.trim()) return arr;
    const q = query.toLowerCase().trim();
    return arr.filter((x) =>
      (x.full_name || "").toLowerCase().includes(q) ||
      (x.email || "").toLowerCase().includes(q) ||
      (x.organization || "").toLowerCase().includes(q) ||
      (x.message || "").toLowerCase().includes(q)
    );
  }, [items, filter, query]);

  const stats = useMemo(() => {
    const out = { total: items.length };
    for (const s of STATUSES) out[s.v] = items.filter((x) => (x.status || "new") === s.v).length;
    return out;
  }, [items]);

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid pt-28 pb-16 px-5 lg:px-8" data-testid="page-admin-demos">
      <SEO title="Admin — Demandes de démo" canonical="/admin/demos" noIndex />
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-7">
          <div>
            <Link to="/admin" className="text-[12px] text-[var(--mc-muted)] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
              <ArrowLeft size={12} /> Retour admin
            </Link>
            <div className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)]">
              <ShieldCheck size={13} /> Super-admin · CRM léger
            </div>
            <h1 className="font-display text-[#0F2B5B] text-3xl lg:text-4xl mt-2">Demandes de démo</h1>
            <p className="mt-1 text-[13px] text-[var(--mc-muted)]">
              {stats.total} demande{stats.total > 1 ? "s" : ""} au total. Cliquez sur un statut pour le modifier.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-demos-refresh"
                    className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-2">
              <RefreshCw size={13} /> Actualiser
            </button>
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="admin-demos-filters">
          <FilterChip active={filter === "all"} label="Toutes" count={stats.total} onClick={() => setFilter("all")} testid="filter-all" />
          {STATUSES.map((s) => (
            <FilterChip key={s.v} active={filter === s.v} label={s.label} count={stats[s.v]} colorClass={s.color}
                        onClick={() => setFilter(s.v)} testid={`filter-${s.v}`} />
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
          <input data-testid="admin-demos-search" className="mc-input w-full pl-9 pr-9"
                 placeholder="Nom, email, organisation, message…"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--mc-muted)] hover:text-[#0F2B5B]">
              <X size={13} />
            </button>
          )}
        </div>

        {loading && <div className="text-[var(--mc-muted)]">Chargement…</div>}
        {err && <div className="text-[#DC2626] text-[13px]">{err}</div>}

        {!loading && !err && (
          filtered.length === 0 ? (
            <div className="mc-card p-12 text-center">
              <div className="mx-auto grid place-items-center h-12 w-12 rounded-full bg-[rgba(127,179,255,0.10)] border border-[rgba(127,179,255,0.18)] text-[var(--mc-muted)] mb-3">
                <MessageSquare size={18} />
              </div>
              <p className="text-[var(--mc-muted)] text-[13px]">Aucune demande pour ce filtre.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map((r) => <DemoRow key={r.id} req={r} onStatusChange={(s) => updateStatus(r.id, s)} onDelete={() => deleteOne(r.id, r.full_name)} />)}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, label, count, colorClass, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`px-3.5 py-1.5 rounded-full text-[11.5px] font-medium inline-flex items-center gap-1.5 border transition ${
              active
                ? `${colorClass || "bg-[var(--mc-blue-500)] border-[var(--mc-blue-500)] text-white"}`
                : "bg-transparent border-[rgba(127,179,255,0.18)] text-[var(--mc-muted)] hover:text-[#0F2B5B] hover:border-[rgba(127,179,255,0.32)]"
            }`}>
      {label}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-[rgba(255,255,255,0.18)]" : "bg-[rgba(127,179,255,0.10)]"}`}>{count}</span>
    </button>
  );
}

function DemoRow({ req, onStatusChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const status = req.status || "new";
  const meta = META[status] || META.new;
  return (
    <article className="mc-card p-4 lg:p-5" data-testid={`admin-demo-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <select value={status} onChange={(e) => onStatusChange(e.target.value)}
                    data-testid={`admin-demo-status-${req.id}`}
                    className={`appearance-none cursor-pointer text-[10.5px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 pr-7 rounded-full border ${meta.color}`}
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L1 3h8z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
              {STATUSES.map((s) => <option key={s.v} value={s.v} className="bg-[#0F172A]">{s.label}</option>)}
            </select>
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--mc-muted)]">
              <Calendar size={11} />
              {new Date(req.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
            </span>
            {req.email_sent && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[var(--mc-mint)]">
                <Check size={10} /> Email envoyé
              </span>
            )}
          </div>
          <h3 className="font-display text-[#0F2B5B] text-[16px]">{req.full_name}</h3>
          <div className="text-[12.5px] text-[var(--mc-muted)] mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
            <a href={`mailto:${req.email}`} className="inline-flex items-center gap-1.5 text-[#0284C7] hover:underline">
              <Mail size={12} /> {req.email}
            </a>
            {req.phone && (
              <a href={`tel:${req.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#0F2B5B]">
                <Phone size={12} /> {req.phone}
              </a>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Building2 size={12} /> {req.organization}
              {req.role && <> · {req.role}</>}
            </span>
            {req.audience && (
              <span className="inline-flex items-center gap-1.5 text-[var(--mc-mint)]">
                <ShieldCheck size={11} /> {req.audience}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {req.message && (
            <button onClick={() => setOpen((o) => !o)}
                    data-testid={`admin-demo-toggle-${req.id}`}
                    className="btn-ghost px-3 py-1.5 rounded-full text-[11.5px] inline-flex items-center gap-1.5">
              <MessageSquare size={11} /> {open ? "Masquer" : "Voir"} message
            </button>
          )}
          <button onClick={onDelete} data-testid={`admin-demo-delete-${req.id}`}
                  className="px-3 py-1.5 rounded-full text-[11.5px] border border-[rgba(255,120,120,0.32)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.10)] inline-flex items-center gap-1.5">
            <Trash2 size={11} /> Supprimer
          </button>
        </div>
      </div>
      {open && req.message && (
        <div className="mt-3 rounded-lg border border-[rgba(127,179,255,0.16)] bg-[#F8FAFC] p-4">
          <p className="text-[13px] text-[#334155] whitespace-pre-wrap leading-relaxed">{req.message}</p>
        </div>
      )}
    </article>
  );
}
